import {err, ok, ValidationError} from 'framework';
import type {Result, Validator} from 'framework';
import * as z from 'zod';
import type {LogSetInput} from '../ports/workout.repository';
import {WorkoutExerciseIdValidator} from './workout-exercise-id.validator';
import {WorkoutIdValidator} from './workout-id.validator';
import {WorkoutSetIdValidator} from './workout-set-id.validator';

export class LogSetDataValidator implements Validator<LogSetInput> {
  private static readonly workoutId = new WorkoutIdValidator();
  private static readonly workoutExerciseId = new WorkoutExerciseIdValidator();
  private static readonly workoutSetId = new WorkoutSetIdValidator();

  readonly standardSchema = z.strictObject({
    workoutId: LogSetDataValidator.workoutId.standardSchema,
    workoutExerciseId: LogSetDataValidator.workoutExerciseId.standardSchema,
    setId: LogSetDataValidator.workoutSetId.standardSchema.optional(),
    weightKg: z.number({error: 'Weight is required.'}).min(0).max(2000),
    reps: z.number().int().min(0).max(1000),
    rpe: z.number().min(0).max(10).nullable().optional(),
    isWarmup: z.boolean().optional(),
    isDropset: z.boolean().optional(),
    isFailure: z.boolean().optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
  });

  readonly formSchema = z.strictObject({
    workoutId: LogSetDataValidator.workoutId.standardSchema,
    workoutExerciseId: LogSetDataValidator.workoutExerciseId.standardSchema,
    setId: LogSetDataValidator.workoutSetId.standardSchema.optional(),
    weightKg: z.number({error: 'Weight is required.'}).min(0).max(2000),
    reps: z.number().int().min(0).max(1000),
    isWarmup: z.boolean(),
  });

  validate(value: unknown): Result<LogSetInput, ValidationError> {
    const parsed = this.standardSchema.safeParse(value);
    if (!parsed.success) {
      return err(new ValidationError(parsed.error.issues[0]?.message ?? 'Invalid log set data.'));
    }
    return ok(parsed.data);
  }
}
